<template>
  <EXTooltip
    v-if="featureEnabled && tooltipTitle"
    placement="top"
    :title="tooltipTitle"
    :delay="200"
  >
    <span :class="['inspection-video-watch-status-badge', variantClass]">
      {{ label }}
    </span>
  </EXTooltip>
  <span
    v-else-if="featureEnabled"
    :class="['inspection-video-watch-status-badge', variantClass]"
  >
    {{ label }}
  </span>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { formatInspectionDate } from "@components/utils/buildCheckpointDrawerSections";
import { LD_FLAG_IDEA_MDP_452 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11341 — InspectionVideoWatchStatusBadge.
 *
 * Read-only pill badge reporting whether the employee has watched the
 * checkpoint's microlearning video. Rendered only on video-based checkpoints,
 * as the second badge in the MetaRow of AsEmployeeCheckpointInspectionCard.
 *
 * ER-11856 adds a hover tooltip (`By {employee} on {microlearning_completed_on}`)
 * that only appears once the video has been watched and the server exposes the
 * facet timestamp. No events, no slots, no click target. Shipped behind the
 * linked idea flag (idea-MDP-452, New Checkpoint Edit and Inspection).
 */
const props = defineProps({
  learningSeen: {
    type: Boolean,
    required: true,
  },
  /** Timestamp the employee completed the microlearning (ER-11856). */
  microlearningCompletedOn: {
    type: String,
    default: null,
  },
  /** Employee's own name — used for the video/quiz tooltips (no distinct actor). */
  employeeName: {
    type: String,
    default: "",
  },
});

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_452, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

const label = computed(() => (props.learningSeen ? "✓ Video watched" : "Video not Watched"));

const variantClass = computed(() =>
  props.learningSeen
    ? "inspection-video-watch-status-badge--watched"
    : "inspection-video-watch-status-badge--unwatched"
);

const tooltipTitle = computed(() => {
  const name = String(props.employeeName || "").trim();
  const date = formatInspectionDate(props.microlearningCompletedOn);
  if (!name || !date) return "";
  return `By ${name} on ${date}`;
});
</script>

<style scoped>
.inspection-video-watch-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  cursor: default;
  user-select: none;
  padding: var(--spacing-xxs, 2px) var(--spacing-md, 8px);
  border: 1px solid;
  border-radius: 9999px;
}

.inspection-video-watch-status-badge--unwatched {
  color: var(--component-colors-utility-gray-utility-gray-700, #344054);
  background: var(--component-colors-utility-gray-utility-gray-50, #f9fafb);
  border-color: var(--component-colors-utility-gray-utility-gray-200, #eaecf0);
}

.inspection-video-watch-status-badge--watched {
  color: var(--component-colors-utility-success-utility-success-700, #067647);
  background: var(--component-colors-utility-success-utility-success-50, #ecfdf3);
  border-color: var(--component-colors-utility-success-utility-success-200, #aaefc6);
}
</style>
